import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addVoter,
  addVotersToList,
  fetchFilteredList,
} from "../../features/voterIdLookupSlice/voterIdLookupApi";
import {
  moveToPage,
  setPerPage,
  resetFilters,
  toggleColumn,
  toggleTemporaryData,
  updateFilters,
  setSort,
  setColumns,
  toggleSelectRow,
  toggleAllRows,
} from "../../features/voterIdLookupSlice/voterIdLookupSlice";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import AddVotersModal from "../MyList/AddVotersModal";
import useMemory from "../../hooks/useMemory";
import Table from "../../components/table/Table";
import VoterIdLookupSteps from "../../components/steps/VoterIdLookupSteps";
import TableButton from "../../components/table/TableButton";
import {
  addVoterToList,
  updateList,
} from "../../features/myListSlice/myListSlice";
import Loading from "../../components/Loading";
import { toast } from "react-toastify";

const LookupTable = () => {
  const [isShowingTutorial, setIsShowingTutorial] = useState(false);
  const [isSpinner, setIsSpinner] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [_, setSearchParams] = useSearchParams();
  const state = useSelector((state) => state.voterIdLookup);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  // useMemory("voter-id-lookup-memory", state);

  const addedTempData = useRef(false);

  useUpdateEffect(() => {
    dispatch(fetchFilteredList({ navigate, params: { page: 1 } }));
  }, [state.sort]);

  const handleSort = (sorts) => {
    dispatch(setSort(sorts));
  };

  const handleChangePage = (toPage) => {
    dispatch(moveToPage(toPage));
    dispatch(fetchFilteredList({ navigate }));
  };

  const handleFilters = (key, value) => dispatch(updateFilters([key, value]));

  const handleToggleColumn = (key) => dispatch(toggleColumn(key));

  const handleResetFilters = async () => {
    let index = state.columns.findIndex((ele)=> ele.key === 'school_district');
    if (state.columns[index].filter.value) {
      setSearchParams({page:1});
      await dispatch(resetFilters());
      await dispatch(updateFilters(['school_district', 21]));
      await dispatch(fetchFilteredList({ navigate, params: { page: 1 } }));
    } else {
      setSearchParams({page:1});
      dispatch(resetFilters());
    }
  };

  const handleChangePerPage = (perPage) => dispatch(setPerPage(perPage));

  const handleSubmit = () => {
    dispatch(fetchFilteredList({ navigate, params: { page: 1 } }));
  };

  const handleStartTutorial = () => {
    setIsShowingTutorial(true);
    if (state.filteredList.length === 0) {
      addedTempData.current = true;
      dispatch(toggleTemporaryData());
    }
  };

  const handleReorder = (columns) => {
    const prevMemory = JSON.parse(
      localStorage.getItem("voter-id-lookup-memory")
    );

    localStorage.setItem(
      "voter-id-lookup-memory",
      JSON.stringify({ ...prevMemory, columns })
    );

    dispatch(setColumns(columns));
  };

  const listNames = useSelector((state) => state.myList.listNames);
  console.log('Redux listNames:', listNames);

  // Ensure we have an array to work with
  const safeListNames = Array.isArray(listNames) ? listNames : [];
  console.log('Safe listNames:', safeListNames);

  // Get stored list names safely
  let storedListNames = [];
  try {
    const stored = localStorage.getItem('listNames');
    if (stored) {
      const parsed = JSON.parse(stored);
      storedListNames = Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Error parsing stored listNames:', error);
  }
  console.log('Stored listNames:', storedListNames);

  // Use Redux state as source of truth, fall back to localStorage if needed
  const finalListNames = safeListNames.length > 0 ? safeListNames : storedListNames;
  console.log('Final listNames:', finalListNames);
  const handleAddVoter = async(row) => {
    try {
      setIsSpinner(true);
      const result = await dispatch(
        addVoter({
          navigate,
          voter: row,
          listName: finalListNames[0]
        })
      ).unwrap();
      
      if (result) {
        dispatch(addVoterToList({ voter: row, listName: finalListNames[0] }));
      }
      handleResetFilters();
      setIsSpinner(false);
    } catch (error) {
      console.error('Failed to add voter:', error);
      setIsSpinner(false);
    }
  }
  const handleSelectAllRows = (data) => {
    dispatch(toggleAllRows());
  }
  const handleToggleSelectRow = (row) => {
    dispatch(toggleSelectRow({ id: row.voter_id }));
  }
  const handleAddVotersToList = async (listName) => {
    const obj = state.filteredList.reduce((acc, ele) => {
      if (state.selectedRows.includes(ele.voter_id)) {
        acc[ele.voter_id] = ele;
      }
      return acc;
    }, {});
    await dispatch(addVotersToList({
      navigate,
      voter_ids: state.selectedRows,
      listName: listName,
      voters: obj
    }))
    handleResetFilters();
  }
  return (
    <>
      <VoterIdLookupSteps
        enabled={isShowingTutorial}
        onExit={() => {
          setIsShowingTutorial(false);
          if (addedTempData.current) dispatch(toggleTemporaryData());
          addedTempData.current = false;
        }}
      />
      {isImportModalOpen && (
        <AddVotersModal close={() => setIsImportModalOpen(false)} />
      )}
      {isSpinner ? <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"><Loading /></div> : null}
      <Table
        isEdit={true}
        isListTableName="lookupTable"
        state={{ ...state, data: state.filteredList }}
        onSortChange={handleSort}
        onFiltersChange={handleFilters}
        onToggleColumn={handleToggleColumn}
        onResetFilters={handleResetFilters}
        onChangePage={handleChangePage}
        onChangePerPage={handleChangePerPage}
        onSubmit={handleSubmit}
        onToggleSelectRow={handleToggleSelectRow}
        onSelectAllRows={handleSelectAllRows}
        onStartTutorial={handleStartTutorial}
        onReorder={handleReorder}
        isFocused={!isShowingTutorial}
        tableName={'LookupTable'}
        finalListNames={finalListNames}
        totalItems={state.totalItems}
        addVoters={handleAddVoter}
        handleAddVotersToList={handleAddVotersToList}
        tableActions={[
          {
            id: "add-voter-btn",
            label: "Have a spreadsheet? Upload Here",
            // icon: "/import.svg",
            onClick: () => setIsImportModalOpen(true),
          },
        ]}
        emptyPlaceholder={(onResetFilters) => (
          <>
            {state.status.fetchData === "success" && (
              <div className="w-full text-center">
                <p className="text-xl font-medium">{state.columns.some((c) => c.filter.value) ? 
                  <>
                    <div className="max-w-xl mx-auto mt-10 p-6 bg-white border border-gray-200 rounded-lg shadow-md">
                      <h2 className="text-lg font-semibold text-red-600 mb-4">No matching records found.</h2>
                      <p className="text-gray-700 mb-2">Please try the following:</p>
                      <ul className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                        <li>Confirm that you spelled the names correctly.</li>
                        <li>Confirm that you put the last name in the correct column.</li>
                        <li>Remove the first name and add a street name (without Dr, Ln, Cir, etc.) — just the name.</li>
                        <li>Try adding the zip code.</li>
                        <li>
                          Search for the spouse or adult child of the person. If found, use their address and look
                          for all voters registered at that address.
                        </li>
                      </ul>
                    </div>
                  </>
                 : 'No records found'}</p>
  
                {/* {state.columns.some((c) => c.filter.value) && (
                  <div className="md-lg:absolute md-lg:bottom-0">
                    <TableButton onClick={onResetFilters} icon="/import.svg">
                      Reset
                    </TableButton>
                  </div>
                )} */}
              </div>
            )}

            {/* {state.status.fetchData === "idle" && (
              <div className="w-full text-center md-lg:absolute md-lg:bottom-8">
                <p className="text-xl font-medium">
                  Search for voters using their first and last names in the
                  boxes above.
                </p>
              </div>
            )} */}
          </>
        )}
        rowActions={{
          isDropdown: finalListNames.length > 1,
          triggers: [state.status.addVoter],
          state: state.status.addVoter,
          onClick: async (row) => {
            try {
              const result = await dispatch(
                addVoter({
                  navigate,
                  voter: row,
                  listName: finalListNames[0]
                })
              ).unwrap();
              
              if (result) {
                dispatch(addVoterToList({ voter: row, listName: finalListNames[0] }));
              }
              handleResetFilters();
            } catch (error) {
              console.error('Failed to add voter:', error);
            }
          },
          label: "Add Voter",
          dropdownTitle: null,
          options: finalListNames.map((listName) => ({
            label: listName,
            onClick: async (row) => {
              try {
                const result = await dispatch(
                  addVoter({
                    navigate,
                    voter: row,
                    listName,
                  })
                ).unwrap();
                
                if (result) {
                  dispatch(addVoterToList({ voter: row, listName }));
                }
                handleResetFilters();
              } catch (error) {
                console.error('Failed to add voter:', error);
              }
            },
          })),
          className: (isDisabled) =>
            isDisabled ? "text-black" : "text-cherry",
        }}
      />
    </>
  );
};

export default LookupTable;
